import React, { useEffect, useState, useContext } from 'react';
import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  Selection,
  SelectionMode,
} from '@fluentui/react/lib/DetailsList';
import { MarqueeSelection } from '@fluentui/react/lib/MarqueeSelection';
import { AcceptIcon, ProviderConsumer as FluentUIThemeConsumer } from '@fluentui/react-northstar';
import {
  ApprovalListIcon,
  TaskListIcon,
  TicketListIcon,
} from '../../../../svg';
import EmptySVG from './../../../../svg/empty.svg';
import { MyWorkListDataI } from '../MyWork';
import { ThemeColorScheme } from '../../../shared/common/TeamsTheme';
import {
  SkeletonCell, hierarchialCategoryName, hierarchialPriorityName, hierarchialStatusName, hierarchialTicketType,
} from '../../../shared/components/table/TicketListBoard';
import { LinkRecord, SLAPills, _getUser, _getUserAvatar, _getUsers } from '../../../shared/components/table/ClassDetailList';
import { appState, AppStateValue } from '../../../AppState';
import { ConvertDateToLocale } from '../../../shared/common/ConvertDate';
import { TicketLifecyclePhaseElement } from '../../../shared/components/TicketLifecycleTab/TicketLifecyclePhaseRibbon';
import '../myWork.css'
import { EnumSortOrderType } from '../../toolbar/CustomViewContextProvider';
import { MyWorkContext } from '../MyWorkContextProvider';
import useMyWorkListFunctions from './useMyWorkListFunctions';
import { getSelectedTabFromStorage, getMyWorkSortOrderFromStorage, setMyWorkSortOrderInStorage } from './myWorkListHelper';
import { CustomPropertiesAnswerJsonKey, ListColumnKey, MyWorkListViewType } from '../../../shared/utils/constants';
import { Route } from '../../../../route-constants';
import { useLocation } from 'react-router-dom';
import { EmptyData } from '../../../shared/components/EmptyData';
import { DateTime } from 'luxon';
import { i18n, openGroupChat } from '../../../shared/utils/helper';
import { AsyncCircleImage } from '../../../shared/components/CircleImages';
import { ICustomProperty } from '../../../shared/interfaces/customproperties.interface';
import { getCustomPropertyDisplayData } from '../../../shared/components/TicketDetailInfo/TicketDetailInfo';

const { t } = i18n();

export interface IDetailsListBasicExampleItem {
  key: number;
  name: string;
  value: number;
}

export interface IDetailsListBasicExampleState {
  items: IDetailsListBasicExampleItem[];
  selectionDetails: string;
}

const renderRecord = (
  item: MyWorkListDataI
): { [x: string]: React.ReactElement } => {
return {
  Id: <LinkRecord {...{ticketId: item.TicketId, itemId: item.Id, value: item.TicketId, type: item.ItemType, title: item.TicketTitle }} />,
  Title: <LinkRecord {...{ticketId: item.TicketId, itemId: item.Id, value: item.Title, type: item.ItemType, title: item.Title }} />,
  Type: (
    <div className={`w-full truncate mr-4`} dir='rtl'>
      <bdi>{item.TicketType || '-'}</bdi>
    </div>
  ),
  SLAs: item.TicketSlas?.length > 0 ? <SLAPills ticketSlas={item.TicketSlas} /> : <>-</>,
  Phase: item.LifecyclePhaseName ? (<TicketLifecyclePhaseElement status={item.LifeCycleStatus} phaseName={item.LifecyclePhaseName} />) : (<span>-</span>),
  GroupChat:   <>
      <div style={{ display: 'flex', alignItems: 'center' }}>
        {item.Members && item.Members.length > 0 && (
          <AsyncCircleImage members={item.Members} />
        )}
        {item.GroupChatId ? (
          <a href="#" onClick={() => openGroupChat(item.ConversationId)}>
            {t('ticket.ticket-board.ticket-card.open-chat')}
          </a>
        ) : (
          <span>{t('ticket.ticket-board.ticket-card.no-chat')}</span>
        )}
      </div>
      </>,
  Requester: item.Requester.length > 1  ? _getUsers(item.Requester.map(r => ({ PlatformUserId: r.Id, PlatformUser: r }))) : 
             item.Requester.length == 1 ? _getUser(item.Requester[0]?.AadObjectId ? item.Requester[0]?.AadObjectId : item.Requester[0]?.UserName, item.Requester[0]?.FullName) : <>-</>,
  Assignee: item.Assignee.length > 1  ? _getUsers(item.Assignee.map(r => ({ PlatformUserId: r.Id, PlatformUser: r }))) : 
            item.Assignee.length == 1 ? _getUser(item.Assignee[0]?.AadObjectId ? item.Assignee[0]?.AadObjectId : item.Assignee[0]?.UserName, item.Assignee[0]?.FullName) : <>-</>,
  'Last Modified Date': (<span>{ConvertDateToLocale(item.LastModified, DateTime.DATE_SHORT)}</span>),
  'Created Date': <span>{ConvertDateToLocale(item.CreatedDate, DateTime.DATE_SHORT)}</span>,
  'Due Date': <span>{item.DueDate ? ConvertDateToLocale(getDateOrEmptyString(item.DueDate), DateTime.DATE_SHORT): '-'}</span>,
  'Resolution Date': <span>{item.ResolutionDate ? ConvertDateToLocale(getDateOrEmptyString(item.ResolutionDate), DateTime.DATE_SHORT): '-'}</span>,
  'ResolutionCategory': <span>{item.ResolutionCategory ? item.ResolutionCategory : '-'}</span>,
  'Note': <span>{item.Note ? item.Note : '-'}</span>,
  'InternalNote': <span>{item.InternalNote ? item.InternalNote : '-'}</span>,
  'DraftKB': <span>{item.DraftKB ? <AcceptIcon />: ''}</span>,
}};

const getDateOrEmptyString = (date: string | null) => date ? date : '';

const renderCPRecordValue =
  (customProperty: ICustomProperty) => (item: MyWorkListDataI) => {
    const answerValues = JSON.parse((item.CustomPropertiesAnswerJson?.length ?? 0) > 0 ? item.CustomPropertiesAnswerJson :  "{}");
    const displayValue = getCustomPropertyDisplayData(answerValues, customProperty);
    return (
      <div className={`w-full mr-4`} dir="rtl">
        <bdi>{displayValue}</bdi>
      </div>
    );
  };

const renderRecordValue = (colName: string) => (item: MyWorkListDataI) => {
  return item.Title === 'temp_ticket' ? (
    <SkeletonCell />
  ) : (
    renderRecord(item)[colName]
  );
};

// name: [ key, name, fieldName, minWidth, maxWidth, isMultiline, isPadded, onRender ]
const _requestLength = (localStorage.getItem('TikitRequestLength') || 'abbreviated') == 'full';
const _cols: _ColsType = {
  Icon: [ListColumnKey.Icon, '', 'Icon', 10, 10, null, null, null],
  Id: [ListColumnKey.Id, t('ticket.ticket-list.id'), 'Id', 30, 30, null, true, renderRecordValue('Id')],
  Type: [ListColumnKey.Type, t('ticket.ticket-list.type'), 'TicketType', 100, 100, true, true, renderRecordValue('Type')],
  Title: [ListColumnKey.Title, t('ticket.ticket-list.title'), 'Title', (_requestLength ? 100 : 200), (_requestLength ? 200 : null), (_requestLength ? true : null), true, renderRecordValue('Title')],
  StatusName: [ListColumnKey.Status, t('ticket.ticket-list.status'), 'StatusName', 125, 125, null, null, null],
  GroupChat: [ListColumnKey.GroupChat, t('ticket.ticket-list.group-chat'), 'GroupChat', 100, 100, null, null, renderRecordValue('GroupChat')],
  Requester: [ListColumnKey.Requester, t('ticket.ticket-list.requester'), 'Requester', 150, 150, null, null, renderRecordValue('Requester')],
  Category: [ListColumnKey.Category, t('ticket.ticket-list.category'), 'Category', 125, 125, null, null, null],
  Priority: [ListColumnKey.Priority, t('ticket.ticket-list.priority'), 'Priority', 100, 100, null, null, null],
  SLAs: [ListColumnKey.SLA, t('ticket.ticket-list.sla'), 'SLA', 100, 100, null, null, renderRecordValue('SLAs')],
  Team: [ListColumnKey.Team, t('ticket.ticket-list.team'), 'TeamName', 100, 100, null, null, null],
  Group: [ListColumnKey.Group, t('ticket.ticket-list.group'), 'GroupName', 100, 100, null, null, null],
  Assignee: [ListColumnKey.Assignee, t('ticket.ticket-list.assignee'), 'Assignee', 100, 100, null, true, renderRecordValue('Assignee')],
  Lifecycle: [ListColumnKey.Lifecycle, t('ticket.ticket-list.lifecycle'), 'LifecycleName', 100, 100, null, null, null],
  Phase: [ListColumnKey.Phase, t('ticket.ticket-list.phase'), 'LifecyclePhaseName', 100, 100, null, null, renderRecordValue('Phase')],
  'Last Modified Date': [ListColumnKey.LastModifiedDate, t('ticket.ticket-list.last-modified-date'), 'LastModified', 100, 100, null, null, renderRecordValue('Last Modified Date')],
  'Created Date': [ListColumnKey.CreatedDate, t('ticket.ticket-list.created-date'), 'CreatedDate', 100, 100, null, null, renderRecordValue('Created Date')],
  'Due Date': [ListColumnKey.DueDate, t('ticket.ticket-list.due-date'), 'DueDate', 100, 100, null, null, renderRecordValue('Due Date')],
  'Resolution Date': [ListColumnKey.ResolutionDate, t('ticket.ticket-list.resolution-date'), 'ResolutionDate', 100, 100, null, null, renderRecordValue('Resolution Date')],
   ResolutionCategory : [ListColumnKey.ResolutionCategory, t('resolution-note.resolution-category'), 'ResolutionCategory', 100, 100, null, null, renderRecordValue('ResolutionCategory')],
   Note: [ListColumnKey.Note, t('resolution-note.resolution-note'), 'Note', (_requestLength ? 100 : 200), (_requestLength ? 100 : 200), (_requestLength ? true : null), true, renderRecordValue('Note')],
   InternalNote: [ListColumnKey.InternalNote, t('resolution-note.resolution-note-internal'), 'InternalNote', (_requestLength ? 100 : 200), (_requestLength ? 100 : 200), (_requestLength ? true : null), true, renderRecordValue('InternalNote')],
   DraftKB: [ListColumnKey.DraftKB, t('resolution-note.draft-kb'), 'DraftKB', 100, 100, null, null, renderRecordValue('DraftKB')]
};

const IconObj: {
  [key: string]: React.SVGAttributes<SVGElement>;
} = {
  [MyWorkListViewType.Tickets]: <TicketListIcon />,
  [MyWorkListViewType.Tasks]: <TaskListIcon width={16} height={16}/>,
  [MyWorkListViewType.Approvals]: <ApprovalListIcon />,
};

const _onColumnClick = (
  onColumnClickCallback: (column: IColumn) => void
) => (
  ev: React.MouseEvent<HTMLElement>,
  column: IColumn
): void => {
  const NON_SORTABLE_COLUMNS = [ListColumnKey.SLA, ListColumnKey.Assignee, ListColumnKey.DraftKB, ListColumnKey.GroupChat];
  if(NON_SORTABLE_COLUMNS.some(x => Number(x) == Number(column.key))) return;

  onColumnClickCallback(column);
};

const _colsWithProps = (currentState: AppStateValue, isCustomPropertiesEnabled: boolean) => {
    if(isCustomPropertiesEnabled){
      let customPropertyColId = 1000;
      currentState?.customProperties.filter(p => !p.IsHidden).forEach(cp => {
        _cols[cp.PropertyId] = [customPropertyColId + cp.Id, cp.Name, `${CustomPropertiesAnswerJsonKey}.${cp.PropertyId}`, 100, 100, null, null, renderCPRecordValue(cp)];
      });
    }
    return _cols;
  }

const _columns = (
  myWorkStates: MyWorkStatesType,
  onColumnClickCallback: (column: IColumn) => void,
  isMyGroupWork: boolean,
  currentState: AppStateValue
): IColumn[] => {
  const selectedTabId = getSelectedTabFromStorage(isMyGroupWork) ?? myWorkStates.myWorkList.selectedTabId;
  
  const sortOrder = getMyWorkSortOrderFromStorage(isMyGroupWork, selectedTabId);
  const orderBy = sortOrder?.orderBy || myWorkStates.sortOrder?.[selectedTabId]?.orderBy;
  const orderType = sortOrder?.orderType || myWorkStates.sortOrder?.[selectedTabId]?.orderType;

  return Object.values(_colsWithProps(currentState, myWorkStates.useCustomProperties)).map((value) => {
    return {
      key: value[0] + '',
      name: value[1],
      fieldName: value[2],
      minWidth: value[3],
      maxWidth: value[4],
      isMultiline: value[5],
      isPadded: value[6],
      ...(value[7] && { onRender: value[7] }),
      isSorted: value[0] ===  orderBy ? true : null,
      isSortedDescending: value[0] === orderBy && orderType === EnumSortOrderType.desc ? true : false,
      onColumnClick: _onColumnClick(onColumnClickCallback),
      isResizable: true,
      isCollapsible: true,
    };
  });
};

const noDataFoundObj = {
  [MyWorkListViewType.All]: t('my-work.my-ticket.no-data-all'),
  [MyWorkListViewType.Tickets]: t('my-work.my-ticket.no-ticket'),
  [MyWorkListViewType.Tasks]: t('my-work.my-ticket.no-task'),
  [MyWorkListViewType.Approvals]: t('my-work.my-ticket.no-approval'),
}

const DetailsListTable = () => {
  const [selection, setSelection] = useState<Selection | undefined>();
  function _getSelectionDetails(): string {
    const selectionCount = selection ? selection.getSelectedCount() : 0;

    switch (selectionCount) {
      case 0:
        return 'No items selected';
      case 1:
        return (
          '1 item selected: ' +
          (selection.getSelection()[0] as IDetailsListBasicExampleItem).name
        );
      default:
        return `${selectionCount} items selected`;
    }
  }
  const currentstate = appState();
  const { myWorkStates, setMyWorkStates } = useContext(MyWorkContext);
  const { getpaginatedMyWorkList } = useMyWorkListFunctions(currentstate);
  const [state, setState] = useState({
    items: myWorkStates.myWorkList.allData,
    selectionDetails: _getSelectionDetails(),
  });
  const location = useLocation();

  const isMyGroupWork = location.pathname.includes(Route.myGroupWork.pagePath);

  const [columns, setColumns] = useState([] as IColumn[]);

  const onColumnClickCallback = (column: IColumn) => {
    const sortOrder = {
      orderBy: Number(column.key) as ISortOrderBy,
      orderType: column.isSortedDescending ? EnumSortOrderType.asc : EnumSortOrderType.desc,
    } as ISortOrder;
    
    const selectedTabId = getSelectedTabFromStorage(isMyGroupWork) ?? myWorkStates.myWorkList.selectedTabId;
    
    setMyWorkSortOrderInStorage(isMyGroupWork, selectedTabId, sortOrder);
    setMyWorkStates((prev) => ({
      ...prev,
      sortOrder: {
        ...prev.sortOrder,
        [selectedTabId]: sortOrder,
      },
      myWorkList: {
        ...prev.myWorkList,
        currentPage: 1
      }
    }));

    getpaginatedMyWorkList({ sortOrder, page: 1, selectedTabNumber: selectedTabId });
  }

  useEffect(() => {
    const _selection: Selection = new Selection({
      onSelectionChanged: () =>
        setState((prev) => {
          return { ...prev, selectionDetails: _getSelectionDetails() };
        }),
    });
    setSelection(_selection);

    setColumns(_columns(myWorkStates, onColumnClickCallback, isMyGroupWork, currentstate));
  }, []);

  useEffect(() => {
    if([MyWorkListViewType.Tasks, MyWorkListViewType.Approvals].includes(getSelectedTabFromStorage(isMyGroupWork) || myWorkStates.myWorkList.selectedTabId)) {
      setColumns((prev) => {
        const copiedColumns = prev.slice();
        const typeColumnIdx = copiedColumns.findIndex(column => Number(column.key) === Number(ListColumnKey.Type));
        copiedColumns.splice(typeColumnIdx, 1);

        return copiedColumns;
      })
    }

    const myWorkListdata = [...(myWorkStates.myWorkList.allData || [])];
    const workList = myWorkListdata.map((item) => {
      const requesters = [item.Requester, ...item.AffectedPlatformUsers];
      const assignee = [...item.AssigneeUsers, ...item.TicketCollaborators];
      
      return {
        ...item,
        Icon: IconObj[item.ItemType],
        StatusName: item.ItemType == MyWorkListViewType.Tickets ? hierarchialStatusName({ statusId: item.StatusId, ticketStatuses: currentstate.ticketStatus }) : item.StatusName,
        GroupChat: item.GroupChat,
        Requester: requesters.filter(x => x),
        Assignee: assignee.filter(x => x), 
        TeamName: item['TeamId'] ? item.TeamName : '-',
        GroupName: item['SupportGroupId'] ? item.SupportGroupName : '-',
        TicketType: item.ItemType === MyWorkListViewType.Approvals ? t('ticket-details.edit-ticket-form.approval.approval') :
                    item.ItemType === MyWorkListViewType.Tasks     ? t('ticket-details.edit-ticket-form.task.task') : 
                    item.TypeId                                    ? hierarchialTicketType({ ticketTypeId: item.TypeId, ticketTypes: currentstate.ticketTypes }) : item.TypeName ?? '-',
        Category: item.CategoryId ? hierarchialCategoryName({ categoryId: item.CategoryId, categories: currentstate.categories }) : '-',
        Priority: item.PriorityId ? hierarchialPriorityName({ priorityId: item.PriorityId, priorities: currentstate.priority }) : '-',
        TicketLifecycleName: item.TicketLifecycle?.Title || '-',
        LifeCycleStatus: { Guid: item.LifecycleStatusId },
        Note: item.Note ?? '-',
        InternalNote: item.InternalNote ?? '-',
        ResolutionCategory: item.ResolutionCategory ?? '-',
        DraftKB: item.DraftKB
      };
    });
    setState((prev) => {
      return { ...prev, items: workList };
    });
  }, [myWorkStates.myWorkList]);

  if((myWorkStates.myWorkList?.allData?.length ?? 0) <= 0){
    return <EmptyData
      headerText={noDataFoundObj[getSelectedTabFromStorage(isMyGroupWork) || myWorkStates.myWorkList.selectedTabId]}
      SVGIcon={<EmptySVG height={200} width={200} />}
    />
  }

  const getLayoutMode = () => {
    return (localStorage.getItem("TikitViewsHorizontalScroll") ?? "true") === "true"
      ? DetailsListLayoutMode.fixedColumns
      : DetailsListLayoutMode.justified;
  };

  return selection ? (
    <FluentUIThemeConsumer
      render={(globalTheme) => (
        <div
          className={`py-3.5 px-2.5 min-h-32 main-ticket-list`}
          style={{
            backgroundColor: 'var(--mgt-theme-background2)',
            ...ThemeColorScheme(globalTheme.siteVariables),
          }}
        >
          <div>
            <MarqueeSelection selection={selection}>
              <DetailsList
                items={state.items}
                compact={false}
                columns={columns}
                selectionMode={SelectionMode.none}
                setKey='multiple'
                layoutMode={getLayoutMode()}
                isHeaderVisible={true}
                selection={selection}
                selectionPreservedOnEmptyClick={true}
                enterModalSelectionOnTouch={true}
                ariaLabelForSelectionColumn='Toggle selection'
                ariaLabelForSelectAllCheckbox='Toggle selection for all items'
                checkButtonAriaLabel='select row'
                className="details-list-table"
              />
            </MarqueeSelection>
          </div>
        </div>
      )}
    />
  ) : (
    <></>
  );
};

export default DetailsListTable;

type ColValuesType = [
  number,
  string,
  string,
  number,
  number,
  boolean | null,
  boolean | null,
  (item: MyWorkListDataI) => JSX.Element
];
type _ColsType = {
  [key: string]: ColValuesType;
};
